/* eslint-disable jsx-control-statements/jsx-use-if-tag */
import React, { useEffect, useState } from 'react';
import { useTheme } from '@material-ui/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { patientPageWidth } from '../../constant';

import {
    GridContainer,
    GridItem,
    Title,
    PatientInfoCard,
    AppointmentCard,
    ProviderInfoCard,
    SessionSummaryCard,
    Header,
    Text,
    Progress,
    PatientSessionCard,
} from '../../components/index';

import {
    GET_NEXT_APPOINTMENT,
    GET_PRESENT_SESSION,
    GET_PUBLIC_OPEN_SESSIONS,
} from '../../services/patient';

import { alertError } from '../../redux/stateActions';

import socket from '../../socket';

const Home = ({ history }) => {
    const theme = useTheme();
    const dispatch = useDispatch();

    const mdUp = useMediaQuery(theme.breakpoints.up('sm'));
    const mobileS = useMediaQuery('(max-width:374.95px)');

    const user = useSelector((state) => state.user);

    const [presentSession, setPresentsession] = useState(null);
    const [date, setDate] = useState(null);

    /*--------------- Get Next Appointment ---------------*/
    const [nextSessions, setNextSessions] = useState(null);

    const {
        data: getNextAppointmentData,
        error: getNextAppointmentError,
        loading: getNextAppointmentLoading,
        refetch: getNextAppointmentRefetch,
    } = useQuery(GET_NEXT_APPOINTMENT, {
        variables: {
            limit: 15,
            id: user.patient.id,
            date: date,
        },
        skip: _.isEmpty(user) || !date,
        pollInterval: 30000,
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only',
    });

    useEffect(() => {
        if (getNextAppointmentError) {
            dispatch(alertError(`Cannot get Next Appointment`));
        }
        if (getNextAppointmentData && getNextAppointmentData?.sessions) {
            const newNextSessions = [];
            getNextAppointmentData.sessions.forEach((session) => {
                if (!dayjs().isSame(session.date, 'day')) {
                    return newNextSessions.push(session);
                }

                // filter today session that endTime not yet pass
                if (
                    dayjs().isBefore(
                        dayjs()
                            .set('hour', session.endTime.split(':')[0])
                            .set('minute', session.endTime.split(':')[1])
                    )
                ) {
                    return newNextSessions.push(session);
                }
            });

            setNextSessions(newNextSessions);
        }
    }, [getNextAppointmentError, getNextAppointmentData]);

    const resetTime = () => {
        setDate(dayjs().format('YYYY-MM-DD'));
    };

    useEffect(() => {
        resetTime();
    }, []);

    /*--------------- Get Present Session ---------------*/
    const {
        data: getPresentData,
        error: getPresentError,
        loading: getPresentLoading,
    } = useQuery(GET_PRESENT_SESSION, {
        variables: {
            id: user.patient.id,
        },
        skip: _.isEmpty(user),
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only',
    });

    useEffect(() => {
        if (getPresentError) {
            dispatch(alertError(`Cannot get Present Session Summary`));
        }
        if (getPresentData && getPresentData?.sessions) {
            setPresentsession(getPresentData.sessions[0]);
        }
    }, [getPresentError, getPresentData]);

    /*--------------- Get Next public open meet ---------------*/
    const [publicSessions, setPublicSessions] = useState([]);

    const {
        data: getOpenMeetsData,
        loading: getOpenMeetsLoading,
        error: getOpenMeetsError,
        refetch: getOpenMeetsRefetch,
    } = useQuery(GET_PUBLIC_OPEN_SESSIONS, {
        variables: {
            id: user.patient.id,
            date: date,
            limit: 10,
        },
        skip: _.isEmpty(user) || !date,
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'network-only',
    });

    useEffect(() => {
        if (getOpenMeetsError) {
            dispatch(alertError(`Cannot get open session`));
        }

        if (getOpenMeetsData && getOpenMeetsData.sessions) {
            setPublicSessions(getOpenMeetsData.sessions);
        }
    }, [getOpenMeetsError, getOpenMeetsData]);

    const onSeeAllAppointment = () => {
        history.push('/appointment');
    };

    const onAppointmentDetail = (appointment) => {
        history.push(`/appointment/${appointment.meet.type}/${appointment.id}`);
    };

    const onSeeAllPublic = () => {
        history.push('/public');
    };
    const onPublicDetail = (id) => {
        history.push(`/appointment/open/${id}`);
    };

    const onSeeAllSummary = () => {
        history.push('/summary');
    };

    useEffect(() => {
        socket.on('newSession', () => {
            getNextAppointmentRefetch();
            getOpenMeetsRefetch();
        });
        socket.on('sessionUpdated', () => {
            getNextAppointmentRefetch();
            getOpenMeetsRefetch();
        });
    }, []);

    return (
        <div
            style={{
                paddingBottom: '20px',
            }}
        >
            <Header
                showMessage
                showNotice
                style={{ maxWidth: patientPageWidth, padding: mobileS ? '0 5px' : '0 15px' }}
            />
            <GridContainer padding={mobileS ? '0 5px' : '0 15px'} $maxWidth={patientPageWidth}>
                <GridItem justifyContent="flex-start">
                    <Title $fontSize="18px">{`Welcome back, ${user?.fullname}`}</Title>
                </GridItem>
                <GridItem margin="15px 0 0 0" justifyContent="flex-start">
                    <PatientInfoCard
                        mobileS={mobileS}
                        user={user}
                        $avatarSize={mobileS ? '60px' : mdUp ? '120px' : '80px'}
                        $maxWidth="508px"
                    />
                </GridItem>
            </GridContainer>

            <GridContainer
                margin="15px 0 0 0"
                style={{ width: patientPageWidth, maxWidth: '100vw' }}
            >
                <GridContainer padding={mobileS ? '0 5px' : '0 15px'}>
                    <GridItem justifyContent="flex-start" xs={9} md={9}>
                        <Title $fontSize="18px">Next Appointment</Title>
                    </GridItem>
                    <GridItem justifyContent="flex-end" xs={3} md={3}>
                        <Text $color="pink" onClick={onSeeAllAppointment}>
                            See all
                        </Text>
                    </GridItem>
                </GridContainer>
                <div
                    style={{
                        overflowX: 'auto',
                        display: 'flex',
                        width: '100%',
                        paddingLeft: '15px',
                        position: 'relative',
                    }}
                >
                    {getNextAppointmentLoading && <Progress />}

                    {!_.isEmpty(nextSessions) ? (
                        nextSessions.map((appointment, index) => (
                            <AppointmentCard
                                data={appointment}
                                key={'appointment' + index}
                                onClick={() => onAppointmentDetail(appointment)}
                                $avatarCircle
                                $maxWidth="300px"
                            />
                        ))
                    ) : (
                        <div
                            style={{
                                display: 'flex',
                                width: '100%',
                                height: '120px',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <p> No Information</p>
                        </div>
                    )}
                </div>
            </GridContainer>

            <GridContainer
                margin="15px 0 0 0"
                style={{ width: patientPageWidth, maxWidth: '100vw' }}
            >
                <GridContainer padding={mobileS ? '0 5px' : '0 15px'}>
                    <GridItem justifyContent="flex-start" xs={9} md={9}>
                        <Title $fontSize="18px">Open Session</Title>
                    </GridItem>
                    <GridItem justifyContent="flex-end" xs={3} md={3}>
                        <Text $color="pink" onClick={onSeeAllPublic}>
                            See all
                        </Text>
                    </GridItem>
                </GridContainer>
                <div
                    style={{
                        overflowX: 'auto',
                        display: 'flex',
                        width: '100%',
                        paddingLeft: '15px',
                        position: 'relative',
                    }}
                >
                    {getOpenMeetsLoading && <Progress />}
                    {!_.isEmpty(publicSessions) ? (
                        publicSessions.map((session) => (
                            <PatientSessionCard
                                data={session}
                                key={session.id}
                                $maxWidth="300px"
                                $minWidth="290px"
                                $style={{ margin: '10px 15px 10px 0' }}
                                onClick={() => onPublicDetail(session.id)}
                            />
                        ))
                    ) : (
                        <div
                            style={{
                                display: 'flex',
                                width: '100%',
                                height: '120px',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <p> No Information</p>
                        </div>
                    )}
                </div>
            </GridContainer>

            <div style={{ paddingBottom: '65px' }}>
                <GridContainer
                    margin="15px 0 0 0"
                    padding={mobileS ? '0 5px' : '0 15px'}
                    $maxWidth={patientPageWidth}
                >
                    <GridContainer>
                        <GridItem justifyContent="flex-start" xs={9} md={9}>
                            <Title $fontSize="18px">Present Session Summary</Title>
                        </GridItem>
                        <GridItem justifyContent="flex-end" xs={3} md={3}>
                            <Text $color="pink" onClick={onSeeAllSummary}>
                                See all
                            </Text>
                        </GridItem>
                    </GridContainer>
                </GridContainer>

                <div style={{ position: 'relative' }}>
                    {getPresentLoading && <Progress />}

                    {presentSession && !_.isEmpty(presentSession.patient_summaries) && (
                        <GridContainer
                            padding={mobileS ? '0 5px' : '0 15px'}
                            $maxWidth={patientPageWidth}
                        >
                            <GridItem justifyContent="flex-start">
                                <ProviderInfoCard
                                    data={presentSession.meet.staff}
                                    $maxWidth="508px"
                                />
                            </GridItem>
                        </GridContainer>
                    )}

                    {presentSession && (
                        <GridContainer
                            padding={mobileS ? '0 5px' : '0 15px'}
                            $maxWidth={patientPageWidth}
                        >
                            <GridItem justifyContent="flex-start">
                                <SessionSummaryCard
                                    margin="10px 0 0 0"
                                    title="Session Summary"
                                    data={presentSession}
                                    consultTime
                                />
                            </GridItem>
                        </GridContainer>
                    )}
                </div>
            </div>
        </div>
    );
};

Home.propTypes = {
    history: PropTypes.object,
};

export default Home;
